/**
 * Session hook. JWT lives in an HttpOnly cookie set by /auth/verify — JS
 * can't read it. We learn whether the cookie is valid by calling /auth/me;
 * the response is cached for the lifetime of the SPA.
 */
import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { getCurrentUser } from './api';
import type { User } from './types';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: Error | null;
}

const auth = signal<AuthState>({ user: null, loading: true, error: null });
let started = false;

function refreshAuth(): void {
  auth.value = { ...auth.value, loading: true };
  getCurrentUser()
    .then((user) => {
      auth.value = { user, loading: false, error: null };
    })
    .catch((err: unknown) => {
      auth.value = {
        user: null,
        loading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    });
}

export function useAuth(): AuthState & { refresh: () => void } {
  useEffect(() => {
    if (started) return;
    started = true;
    refreshAuth();
  }, []);
  return { ...auth.value, refresh: refreshAuth };
}

/** Programmatic check (no hook) — used by route guards. */
export function isAuthenticated(): boolean {
  return auth.value.user !== null;
}
